'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type {
  ExternalNetworkDto,
  ScanProfileDto,
} from '@terra/shared-contracts-external-network';
import { Button } from '../../../components/button';
import {
  DEFAULT_SCANNING_SCHEDULE,
  ScanningConfigurationCard,
  type ScanningConfigurationChange,
} from '../../settings/automation/ScanningConfigurationCard';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';
import {
  ExternalNetworkEgressStrategyDialog,
  type ConnectorNetworkPickerRenderProps,
} from './ExternalNetworkEgressStrategyDialog';
import { ExternalNetworkScanProfileDialog } from './ExternalNetworkScanProfileDialog';
import {
  createScanProfileCollection,
  getScanProfileDisplayLabel,
  hasExternalNetworkSchedule,
  resolveDiscoveryScheduleUpdates,
  type ConnectorDnsNetwork,
  type ExternalNetworkSettingsPersistRequest,
} from './external-settings.model';
import { useScanProfileTierLabels } from './useScanProfileTierLabels';

type EditDialog = 'scanProfile' | 'egressStrategy' | null;

export type ExternalNetworkScanningProfile = Pick<
  ScanProfileDto,
  'id' | 'name' | 'systemTier'
>;

export type ExternalNetworkScanningSettingsProps = {
  surface: ExternalNetworkDto;
  isReadOnly: boolean;
  isScanActive: boolean;
  profiles: readonly ExternalNetworkScanningProfile[];
  isProfilesLoading: boolean;
  isConnectorScanEnabled: boolean;
  connectorNetworks: readonly ConnectorDnsNetwork[];
  isUpdating: boolean;
  isAssigningProfile: boolean;
  onPersist: (
    request: ExternalNetworkSettingsPersistRequest,
  ) => Promise<boolean>;
  onAssignScanProfile: (scanProfileId: string) => Promise<boolean>;
  onManageScanProfiles: () => void;
  renderConnectorNetworkPicker: (
    props: ConnectorNetworkPickerRenderProps,
  ) => ReactNode;
};

export function ExternalNetworkScanningSettings({
  surface,
  isReadOnly,
  isScanActive,
  profiles,
  isProfilesLoading,
  isConnectorScanEnabled,
  connectorNetworks,
  isUpdating,
  isAssigningProfile,
  onPersist,
  onAssignScanProfile,
  onManageScanProfiles,
  renderConnectorNetworkPicker,
}: ExternalNetworkScanningSettingsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalSettings.scanning',
  );
  const tCommon = useTranslations('designSystem.common');
  const tierLabels = useScanProfileTierLabels();
  const [editDialog, setEditDialog] = useState<EditDialog>(null);
  const [syncedSchedule, setSyncedSchedule] = useState(surface.schedule);
  const [limitScheduleEnabled, setLimitScheduleEnabled] = useState(() =>
    hasExternalNetworkSchedule(surface.schedule),
  );

  if (syncedSchedule !== surface.schedule) {
    setSyncedSchedule(surface.schedule);
    setLimitScheduleEnabled(hasExternalNetworkSchedule(surface.schedule));
  }

  const scanProfileCollection = useMemo(
    () => createScanProfileCollection({ profiles, tierLabels }),
    [profiles, tierLabels],
  );
  const assignedProfile = useMemo(
    () => profiles.find((profile) => profile.id === surface.scanProfileId),
    [profiles, surface.scanProfileId],
  );
  const scanProfileDisplayName = getScanProfileDisplayLabel({
    profile: assignedProfile,
    tierLabels,
    fallbackName: surface.scanProfileName ?? surface.scanProfileId,
  });
  const editActionLabel = isScanActive
    ? t('scanLockedTooltip')
    : tCommon('edit');

  const handleScanningChange = (change: ScanningConfigurationChange) => {
    if (typeof change.limitScheduleEnabled === 'boolean') {
      setLimitScheduleEnabled(change.limitScheduleEnabled);
    }

    const updates = resolveDiscoveryScheduleUpdates({
      change,
      limitScheduleEnabled,
    });

    if (updates) {
      void onPersist({ updates, showSuccessToast: true });
    }
  };

  const handleDialogOpenChange =
    (dialog: Exclude<EditDialog, null>) => (open: boolean) =>
      setEditDialog(open ? dialog : null);

  return (
    <>
      <VStack alignItems="stretch" gap={8} w="full">
        <SettingsSection
          title={t('profileSection')}
          action={
            isReadOnly ? undefined : (
              <Button
                variant="outline"
                size="xs"
                onClick={onManageScanProfiles}
              >
                {t('manageScanProfiles')}
              </Button>
            )
          }
        >
          <SettingsCard>
            <SettingsRow
              label={t('scanProfile')}
              value={scanProfileDisplayName}
              action={{
                label: editActionLabel,
                onClick: () => setEditDialog('scanProfile'),
                disabled: isScanActive || isProfilesLoading,
              }}
              isReadOnly={isReadOnly}
            />
            <SettingsRow
              label={t('egressStrategy')}
              value={surface.egressStrategy}
              action={{
                label: editActionLabel,
                onClick: () => setEditDialog('egressStrategy'),
                disabled: isScanActive,
              }}
              isReadOnly={isReadOnly}
            />
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title={t('scheduledDiscovery.sectionTitle')}
          description={t('scheduledDiscovery.activeSurfaceHint')}
        >
          <ScanningConfigurationCard
            scheduleOnlyMode
            smokeTestEnabled={false}
            limitScheduleEnabled={limitScheduleEnabled}
            automationSchedule={surface.schedule ?? DEFAULT_SCANNING_SCHEDULE}
            limitScheduleLabel={t('limitSchedule.label')}
            limitScheduleTooltip={t('limitSchedule.tooltip')}
            scheduleDetailsTitle={t('scheduledDiscovery.detailsTitle')}
            disabled={isReadOnly || isScanActive}
            isUpdating={isUpdating}
            onChange={handleScanningChange}
          />
        </SettingsSection>
      </VStack>

      <ExternalNetworkScanProfileDialog
        open={editDialog === 'scanProfile'}
        onOpenChange={handleDialogOpenChange('scanProfile')}
        scanProfileId={surface.scanProfileId}
        collection={scanProfileCollection}
        isUpdating={isAssigningProfile}
        onSubmit={onAssignScanProfile}
      />

      <ExternalNetworkEgressStrategyDialog
        open={editDialog === 'egressStrategy'}
        onOpenChange={handleDialogOpenChange('egressStrategy')}
        surface={surface}
        isConnectorScanEnabled={isConnectorScanEnabled}
        connectorNetworks={connectorNetworks}
        isUpdating={isUpdating}
        onSubmit={(updates) => onPersist({ updates, showSuccessToast: true })}
        renderConnectorNetworkPicker={renderConnectorNetworkPicker}
      />
    </>
  );
}
